import { useEffect, useMemo, useState } from "react";

import AreaRegion, {
  AreaFilesInformation,
} from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import Tile from "@mapmycustomers/shared/types/territory/Tile";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import { useMap, useMapEventHandler } from "@app/component/map/FeaturedMap";

import boundaryCache from "./boundaryCache";
import CachingDataLoader from "./CachingDataLoader";
import dataLoader from "./dataLoader";
import findFileInformationByZoomLevel from "./findFileInformationByZoomLevel";
import getTilesForBounds from "./getTilesForBounds";

interface BoundariesState {
  regions: AreaRegion[];
  tiles: Tile[];
  zoomKey: number;
}

const useBoundaries = (
  areaType: AreaType,
  files: AreaFilesInformation[],
  active: boolean = true
): [regions: AreaRegion[]] => {
  const { map } = useMap();

  // const [renderCount, setRenderCount] = useState(0); // instead of useForceRerender
  const [state, setState] = useState<BoundariesState>({
    regions: [],
    tiles: [],
    zoomKey: map.getZoom()!,
  });

  const cachingLoader = useMemo(() => {
    const loader = new CachingDataLoader(dataLoader, boundaryCache);
    loader.loadListener = (regions, localAreaType, zoomLevel, tiles) => {
      if (localAreaType === areaType) {
        setState((prevState) => {
          // FIXME: use better tiles comparison
          if (prevState.zoomKey !== zoomLevel || prevState.tiles.length !== (tiles?.length ?? 0)) {
            return prevState;
          }
          return { ...prevState, regions };
        });
      }
    };
    return loader;
  }, [areaType]);

  const viewPortListener = useDebouncedCallback([
    () => {
      if (!active) {
        return;
      }

      const bounds = map.getBounds();
      const zoom = map.getZoom();
      if (!bounds || !zoom) {
        return;
      }

      const fileInfo = findFileInformationByZoomLevel(zoom, files);
      if (!fileInfo) {
        return; // nothing to load
      }

      const zoomKey = fileInfo.zoomLevelRange[0];
      if (!fileInfo.tiles) {
        setState((state) => ({ ...state, tiles: [], zoomKey }));
        cachingLoader.loadRegionsForZoomLevel(areaType, zoomKey);
      } else {
        const tiles = getTilesForBounds(bounds, zoomKey);
        setState((state) => ({ ...state, tiles, zoomKey }));
        cachingLoader.loadDataForTiles(areaType, zoomKey, tiles);
      }
    },
    500,
  ]);

  useMapEventHandler("bounds_changed", viewPortListener);
  useEffect(
    () => {
      viewPortListener();
    },
    // we need files dep here in order to trigger viewPortListener once again when files
    // are finally loaded, because sometimes they're not ready when useBoundaries is called
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewPortListener, files]
  );

  return [state.regions];
};

export default useBoundaries;
